import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "../../../Api/Api";
import endpoints from "../../../Api/Endpoints";

const CHART_DAYS_DEFAULT = 7;
const LIST_DAYS_DEFAULT = 7;

export const getDashboardData = createAsyncThunk(
  "dashboard/getDashboardData",
  async (payload = {}, { rejectWithValue }) => {
    const chartDays = payload.chartDays ?? CHART_DAYS_DEFAULT;
    const listDays = payload.listDays ?? LIST_DAYS_DEFAULT;
    try {
      const settled = await Promise.allSettled([
        API.get(endpoints.ADMIN_STATS, { params: { chartDays } }),
        API.get(endpoints.ADMIN_S3_CORS_HEALTH),
        API.get(endpoints.ADMIN_BID_LOG, {
          params: { page: 1, limit: 20 },
        }),
        API.get(endpoints.ADMIN_PENDING_PRESCRIPTIONS, {
          params: { page: 1, limit: 50 },
        }),
        API.get(endpoints.ADMIN_DOCTORS_PENDING, {
          params: { page: 1, limit: 50, lastDays: listDays },
        }),
        API.get(endpoints.ADMIN_HOSPITALS_PENDING, {
          params: { page: 1, limit: 50, lastDays: listDays },
        }),
        API.get(endpoints.ADMIN_APPROVAL_SUMMARY),
      ]);

      const safeData = (idx) =>
        settled[idx]?.status === "fulfilled" ? settled[idx].value?.data || {} : {};

      const statsPayload = safeData(0);
      const s3CorsHealthPayload = safeData(1);
      const bidPayload = safeData(2);
      const pendingPayload = safeData(3);
      const doctorsPayload = safeData(4);
      const hospitalsPayload = safeData(5);
      const approvalSummaryRaw = safeData(6);
      const approvalSummary = approvalSummaryRaw?.data || approvalSummaryRaw || {};

      const rawChart = statsPayload?.data?.chart || statsPayload?.chart || [];
      const chartData = Array.isArray(rawChart)
        ? rawChart.map((item) => ({
            day: item?.day ?? item?.dayLabel ?? item?.label ?? "",
            patients: Number(item?.patients ?? item?.patientSignups ?? item?.totalPatients ?? 0),
            doctors: Number(item?.doctors ?? item?.doctorSignups ?? item?.totalDoctors ?? 0),
            reverseBids: Number(
              item?.reverseBids ??
                item?.reverseBidRequests ??
                item?.activeReverseBidCount ??
                0
            ),
          }))
        : [];

      const doctorItems = doctorsPayload?.items || [];
      const hospitalItems = hospitalsPayload?.items || [];
      const pendingProviders = [
        ...doctorItems.map((d) => ({
          _id: d._id,
          type: "doctor",
          name: d.name || "Doctor",
          imageUrl: d.imageUrl || "",
          createdAt: d.createdAt,
          adminRating: d.adminRating,
        })),
        ...hospitalItems.map((h) => ({
          _id: h._id,
          type: "hospital",
          name: h.name || "Hospital",
          imageUrl: h.avatarUrl || "",
          createdAt: h.createdAt,
          adminRating: h.adminRating,
        })),
      ];

      return {
        stats:
          statsPayload?.data ||
          statsPayload?.stats ||
          statsPayload ||
          {},

        chartData,

        approvals:
          pendingPayload?.items ||
          pendingPayload?.data ||
          pendingPayload?.approvals ||
          [],

        pendingProviders,
        pendingProviderCount: approvalSummary.pendingProviderCount ?? pendingProviders.length,
        approvedProviderCount: approvalSummary.approvedProviderCount ?? 0,
        rejectedProviderCount: approvalSummary.rejectedProviderCount ?? 0,
        pendingPrescriptionCount: approvalSummary.pendingPrescriptionCount ?? (pendingPayload?.items?.length ?? 0),
        s3CorsHealth: s3CorsHealthPayload,

        activity:
          bidPayload?.items ||
          bidPayload?.data ||
          bidPayload?.activity ||
          [],
      };
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to load dashboard data";

      return rejectWithValue(msg);
    }
  }
);